/**
 * Ce que chaque outil répondrait au lecteur, avant qu'il l'ouvre.
 *
 * UN ANNUAIRE D'OUTILS EST UNE RANGÉE DE PORTES FERMÉES. Toutes les rubriques
 * du genre alignent des cartes qui promettent une réponse, et chacune exige
 * qu'on l'ouvre et qu'on y retape ce qu'on vient de dire ailleurs. Or le
 * lecteur a déjà dit, en haut de la page, ce qu'il veut se verser, ce qu'il
 * fait et quand il a commencé : les cartes qui savent en tirer quelque chose
 * affichent leur réponse sur place, et celles qui pressent passent devant.
 *
 * AUCUNE RÉPONSE N'EST ÉCRITE ICI, ELLES SONT TOUTES CALCULÉES AILLEURS. Ce
 * module ne fait qu'appeler les moteurs que les outils eux-mêmes utilisent,
 * et qu'ils ont chacun leurs tests : statutTva, trimestresRetraite, revenuNet,
 * echeancesPremiereAnnee. Il ne rend que des données, jamais de phrases : les
 * mots sont ceux de la page, dans sa langue.
 *
 * UNE CARTE QUI NE SAIT PAS SE TAIT. Le versement libératoire dépend du revenu
 * fiscal du foyer, que personne n'a donné : sa carte n'affiche que son
 * échéance, pas un verdict. Les échéances passées ne s'affichent pas non plus,
 * un rappel de ce qu'on a manqué n'aidant personne à trier ce qui reste.
 */

import {
    PLAFONDS,
    SEUILS_TVA,
    plafondProratise,
    revenuNet,
    statutTva,
    trimestresRetraite,
} from "./microEnterprise";
import { echeancesPremiereAnnee } from "./deadlines";
import { scenarioReel } from "./businessStructure";
import { comparerFormes, meilleurPartage } from "./salaryDividends";
import { comparerOptionIs } from "./corporateTaxOption";
import { arretEurl, arretSasu } from "./companySickLeave";

/**
 * Les statuts de la phrase d'accueil.
 *
 * UN SOLOPRENEUR N'EST PAS FORCÉMENT EN MICRO. La rubrique a grandi autour de
 * la micro-entreprise, et la phrase d'accueil calculait tout avec ses taux :
 * un gérant d'EURL qui la remplissait lisait un chiffre faux sans le savoir.
 * Le statut ouvre donc la phrase, et tout ce qui suit le lit : la réponse, le
 * curseur, les cartes et la frise.
 *
 *   - micro et projet : les calculs de la micro-entreprise, le projet étant la
 *     micro de celui qui ne s'est pas encore lancé ;
 *   - reel, eurl, sasu : les moteurs du comparateur de régimes et de l'outil
 *     « Rémunération ou dividendes », sur un bénéfice sans dépenses.
 */
export const STATUTS = ["micro", "projet", "reel", "eurl", "eurlIr", "sasu"];
const HORS_MICRO = new Set(["reel", "eurl", "eurlIr", "sasu"]);

/**
 * L'EURL restée à l'impôt sur le revenu est imposée comme une entreprise
 * individuelle au réel : son bénéfice est le revenu de son gérant, sans
 * impôt sur les sociétés ni dividendes. Tous les calculs de la phrase la
 * traitent donc comme le réel.
 */
export const commeLeReel = (statut) => statut === "reel" || statut === "eurlIr";
export const estHorsMicro = (statut) => HORS_MICRO.has(statut);

/**
 * Le barème des indépendants ne connaît pas la Cipav : au réel ou en EURL, un
 * métier réglementé n'a pas de calcul juste, et l'accueil se tait plutôt que
 * de lui en donner un faux. Le président de SASU, lui, relève du régime
 * général quel que soit son métier.
 */
export function statutCalculable(statut, activite) {
    return !(activite === "cipav" && (commeLeReel(statut) || statut === "eurl"));
}

/** Ce qu'un bénéfice laisse hors micro, pour un célibataire sans autre revenu. */
function netHorsMicro({ statut, benefice, activite }) {
    if (commeLeReel(statut)) {
        return scenarioReel({ chiffreAffaires: benefice, charges: 0, activite, artisan: false, foyer: {} }).net;
    }
    return meilleurPartage({ forme: statut, enveloppe: benefice, activite, foyer: {} }).meilleur.net;
}

/**
 * Hors micro, le bénéfice qu'il faut dégager dans l'année pour se verser un
 * montant donné, par dichotomie comme pour la micro. Nul si le statut ne se
 * calcule pas pour cette activité.
 */
export function beneficePourSeVerser({ statut, objectifAnnuel, activite }) {
    if (!statutCalculable(statut, activite)) return null;
    const net = (benefice) => netHorsMicro({ statut, benefice, activite });
    let bas = 0;
    let haut = 2000000;
    if (net(haut) < objectifAnnuel) return null;
    for (let i = 0; i < 40; i++) {
        const milieu = (bas + haut) / 2;
        if (net(milieu) < objectifAnnuel) bas = milieu;
        else haut = milieu;
    }
    return haut;
}

/**
 * Le chiffre d'affaires annuel dont le net tombe sur l'objectif.
 *
 * LE CALCUL SE REMONTE PAR DICHOTOMIE, parce que l'impôt suit un barème par
 * tranches : aucune formule ne donne le chiffre d'affaires à partir du net.
 * Trente itérations descendent sous l'euro sur un intervalle qui part du
 * plafond, et la fonction reste appelable à chaque frappe.
 *
 * Rend null quand même le plafond ne suffit pas : aucun chiffre d'affaires
 * du régime micro n'atteint alors l'objectif.
 */
export function caPourSeVerser({ objectifAnnuel, activite, premiereAnnee }) {
    const plafond = PLAFONDS[activite];
    const netDe = (ca) => {
        const r = revenuNet({ chiffreAffaires: ca, activite });
        // La première année ne paie pas la contribution à la formation.
        return premiereAnnee ? r.net + r.cfp : r.net;
    };
    if (netDe(plafond) < objectifAnnuel) return null;

    let bas = 0;
    let haut = plafond;
    for (let i = 0; i < 30; i++) {
        const milieu = (bas + haut) / 2;
        if (netDe(milieu) < objectifAnnuel) bas = milieu;
        else haut = milieu;
    }
    return haut;
}

/**
 * Le seuil de TVA de chaque activité, rangé comme dans l'outil des seuils :
 * la vente relève du seuil global, tout le reste de celui des prestations, les
 * professions libérales comprises. Le seuil propre aux avocats, auteurs et
 * artistes-interprètes ne concerne aucune des quatre activités de la phrase.
 */
const CATEGORIE_TVA = {
    vente: "global",
    services: "services",
    bnc: "services",
    // « Un métier réglementé » mêle des architectes, soumis aux seuils des
    // prestations, et des psychologues ou des ostéopathes, dont les soins
    // sont exonérés de TVA et ne comptent même pas dans le chiffre comparé
    // aux seuils (reference-2026.json, tva_exoneration_soins et
    // tva_soins_hors_chiffre_franchise). La phrase ne sait pas lequel des deux
    // lit : la carte dit d'abord le cas des soins, puis le verdict des
    // prestations, précédé de « sinon » (METIER_DE_SOIN_POSSIBLE).
    cipav: "services",
};

/** Les activités où le lecteur peut être un soignant, exonéré de TVA. */
const METIER_DE_SOIN_POSSIBLE = new Set(["cipav"]);

/**
 * Les activités où le lecteur peut donner des cours particuliers, exonérés de
 * TVA quand il les donne lui-même, sans salarié, et que ses élèves le paient
 * directement (reference-2026.json, tva_exoneration_cours). Un métier
 * réglementé l'apprend avec les soins ; les autres, par une ligne sous le
 * verdict, quand la TVA arrive.
 */
const COURS_POSSIBLES = new Set(["services", "bnc"]);

/** Une date ISO (AAAA-MM-JJ) lue à midi, pour qu'aucun fuseau ne la décale. */
export function dateDepuisIso(iso) {
    const [a, m, j] = (iso || "").split("-").map(Number);
    if (!a || !m || !j) return null;
    const d = new Date(a, m - 1, j, 12, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Ce que la date de début dit de l'ancienneté, à la place d'une case à cocher.
 *
 * UNE CASE « JE VIENS DE ME LANCER » POUVAIT CONTREDIRE LA DATE : on la cochait
 * avec un début en 2023, ou on l'oubliait avec un début le mois dernier. La
 * date tranche seule, et elle tranche deux choses différentes :
 *   - la contribution à la formation n'est pas due l'année civile de la
 *     création, faute de chiffre d'affaires déclaré l'année précédente ;
 *   - le régime micro est acquis de plein droit les deux premières années
 *     (statutMicro : « premiere » et « deuxieme »).
 * Sans date, on suppose une activité installée : c'est le cas le plus
 * prudent, qui compte la formation et ne promet pas le régime.
 */
export function ancienneteDepuis(dateDebut, aujourdhui = new Date()) {
    if (!(dateDebut instanceof Date) || Number.isNaN(dateDebut.getTime())) {
        return { premiereAnnee: false, regimeAcquis: false };
    }
    const annee = aujourdhui.getFullYear();
    const debut = dateDebut.getFullYear();
    return {
        premiereAnnee: debut === annee,
        regimeAcquis: debut >= annee - 1,
    };
}

/**
 * Le jour où un rythme constant fait passer un seuil.
 *
 * Le chiffre d'affaires cumulé depuis le début du décompte vaut
 * caAnnuel × jours ÷ 365 ; le seuil tombe quand il le dépasse. L'hypothèse
 * d'un rythme constant est celle de la phrase d'accueil, et le texte qui
 * affiche cette date la rappelle.
 */
function jourDuFranchissement(seuil, caAnnuel, debutDecompte) {
    const jours = Math.ceil((seuil * 365) / caAnnuel);
    const d = new Date(debutDecompte);
    d.setDate(d.getDate() + jours);
    d.setHours(12, 0, 0, 0);
    return d;
}

/**
 * Les repères du curseur, en euros nets par mois.
 *
 * Un curseur nu fait jouer, un curseur qui montre où tombent la TVA et le
 * plafond fait comprendre. Les repères sont les montants à se verser qui
 * correspondent exactement aux seuils : au-delà, la zone change de couleur.
 * Ils se calculent sur les seuils PLEINS, même l'année de création : un
 * rythme annuel comparé aux seuils pleins donne le même verdict que le
 * chiffre réduit comparé aux seuils réduits.
 */
export function reperesDuCurseur({ activite, premiereAnnee = false, statut = "micro" }) {
    if (!PLAFONDS[activite]) return null;
    // Hors micro, pas de plafond : seulement les seuils de TVA, qui valent pour
    // tous les régimes, sur un bénéfice sans dépenses.
    if (estHorsMicro(statut)) {
        if (!statutCalculable(statut, activite)) return null;
        const mensuel = (ca) => netHorsMicro({ statut, benefice: ca, activite }) / 12;
        const seuils = SEUILS_TVA[CATEGORIE_TVA[activite]];
        return {
            tvaBase: mensuel(seuils.base),
            tvaMajore: mensuel(seuils.majore),
            plafond: null,
            maximum: Math.ceil(mensuel(seuils.majore * 3) / 100) * 100,
            soins: METIER_DE_SOIN_POSSIBLE.has(activite),
        };
    }
    const mensuel = (ca) => {
        const r = revenuNet({ chiffreAffaires: ca, activite });
        return (premiereAnnee ? r.net + r.cfp : r.net) / 12;
    };
    const categorie = CATEGORIE_TVA[activite];
    const seuils = categorie ? SEUILS_TVA[categorie] : null;
    const plafond = mensuel(PLAFONDS[activite]);
    return {
        tvaBase: seuils ? mensuel(seuils.base) : null,
        tvaMajore: seuils ? mensuel(seuils.majore) : null,
        plafond,
        // Un peu au-delà du plafond, pour que la zone hors micro se voie
        // sans occuper la moitié de la course.
        maximum: Math.ceil((plafond * 1.2) / 100) * 100,
        // Les zones de TVA ne valent pas pour les soins : le curseur le dit.
        soins: METIER_DE_SOIN_POSSIBLE.has(activite),
    };
}

/** L'outil qui traite chaque échéance de la première année. */
const OUTIL_DE_L_ECHEANCE = {
    "acre-demande": "demande-acre",
    "vfl-option": "versement-liberatoire",
    "acre-fin": "acre",
    "cfe-declaration": "cfe-declaration-initiale",
    "cfe-premier-solde": "reclamation-cfe",
};

/** Plus une échéance est proche, plus elle presse. */
function statutDeLEcheance(joursRestants) {
    if (joursRestants <= 30) return "urgent";
    if (joursRestants <= 90) return "attention";
    return "info";
}

/** L'ordre dans lequel les cartes se présentent : ce qui presse d'abord. */
export const RANG_DU_STATUT = { urgent: 0, attention: 1, ok: 2, info: 2 };

/**
 * La carte de l'outil des seuils de TVA, pour un chiffre d'affaires annuel au
 * rythme indiqué. Commune à tous les statuts : la franchise en base vaut pour
 * la micro comme pour l'entreprise individuelle au réel ou la société.
 */
function reponseTva({ activite, ca, dateDebut, aujourdhui }) {
    // La TVA. L'année de création a des seuils réduits au prorata de
    // ses jours d'activité, et un chiffre d'affaires réduit dans la
    // même proportion : le verdict serait le même avec les seuils
    // pleins, mais pas les montants affichés. Annoncer « vous dépassez
    // 41 250 € » à qui a commencé en juillet, et n'encaissera que
    // 19 600 € d'ici décembre, serait vrai en effet et faux en
    // apparence. On lui montre donc SON seuil, réduit.
    if (CATEGORIE_TVA[activite]) {
        const debutCetteAnnee =
            dateDebut instanceof Date &&
            dateDebut.getFullYear() === aujourdhui.getFullYear()
                ? dateDebut
                : null;
        const caCetteAnnee = debutCetteAnnee
            ? (ca * plafondProratise(0, debutCetteAnnee).jours) / 365
            : ca;
        const tva = statutTva({
            categorie: CATEGORIE_TVA[activite],
            caEnCours: caCetteAnnee,
            debutEnCours: debutCetteAnnee,
        });

        // LA DATE OÙ LES PRIX CHANGENT. « Vous passerez le seuil cette
        // année » est juste et abstrait ; le jour où chaque facture
        // doit porter la TVA ne l'est pas. Le décompte part du début
        // d'activité l'année de création, du 1er janvier sinon, et le
        // seuil est celui de l'année, réduit s'il le faut.
        const debutDecompte =
            debutCetteAnnee || new Date(aujourdhui.getFullYear(), 0, 1, 12);
        let dates = {};
        if (tva.statut === "assujetti_immediat") {
            const jour = jourDuFranchissement(tva.majore, ca, debutDecompte);
            dates = {
                date: jour,
                // Une date déjà passée se dit au conditionnel : elle
                // repose sur un rythme supposé, pas sur des factures.
                passe: jour < aujourdhui,
                joursRestants: Math.ceil((jour - aujourdhui) / 86400000),
            };
        } else if (tva.statut === "bascule_janvier_prochain") {
            const effet = new Date(aujourdhui.getFullYear() + 1, 0, 1, 12);
            dates = {
                date: effet,
                passe: false,
                joursRestants: Math.ceil((effet - aujourdhui) / 86400000),
            };
        }

        let statutTvaAffiche = {
            franchise: caCetteAnnee >= tva.base * 0.8 ? "attention" : "ok",
            bascule_janvier_prochain: "attention",
            assujetti_immediat: "urgent",
        }[tva.statut];
        // Un verdict qui ne vaut que « sinon » ne peut pas crier au
        // soignant qu'il est en retard sur une TVA qu'il ne doit pas.
        const soins = METIER_DE_SOIN_POSSIBLE.has(activite);
        if (soins && statutTvaAffiche === "urgent") statutTvaAffiche = "attention";
        if (statutTvaAffiche) {
            return {
                statut: statutTvaAffiche,
                type:
                    tva.statut === "assujetti_immediat" && dates.passe
                        ? "tva_assujetti_immediat_passe"
                        : `tva_${tva.statut}`,
                base: tva.base,
                majore: tva.majore,
                avantBase: tva.avantBase ?? null,
                reduit: Boolean(tva.prorata?.applique),
                // La conséquence en prix ne vaut que si la TVA arrive.
                consequence: tva.statut !== "franchise",
                soins,
                cours: COURS_POSSIBLES.has(activite),
                ...dates,
            };
        }
    }
    return null;
}

/**
 * Hors micro : la TVA, qui vaut pour tous, et les outils des sociétés.
 *
 * Les outils de la micro (plafonds, versement libératoire, ACRE à demander)
 * ne répondent pas ici : leurs règles ne s'appliquent pas, et une carte qui
 * affiche un chiffre de micro à un gérant d'EURL serait fausse sans le dire.
 */
function reponsesHorsMicro({ statut, activite, objectifMensuel, dateDebut, aujourdhui }) {
    const reponses = {};
    if (!activite || !(objectifMensuel > 0)) return reponses;
    const benefice = beneficePourSeVerser({ statut, objectifAnnuel: objectifMensuel * 12, activite });
    if (benefice === null) return reponses;

    // Aucune dépense supposée : le chiffre d'affaires vaut le bénéfice.
    const tva = reponseTva({ activite, ca: benefice, dateDebut, aujourdhui });
    if (tva) reponses["seuils-tva"] = tva;

    if (commeLeReel(statut)) {
        // Dans l'outil de l'option, « eurl » est justement l'EURL à l'IR.
        const c = comparerOptionIs({ forme: statut === "reel" ? "ei" : "eurl", benefice, activite, foyer: {} });
        reponses["passer-a-l-is"] = {
            statut: "ok",
            type: `is_${c.gagnant || "egal"}`,
            gain: Math.abs(c.gain),
        };
        return reponses;
    }

    const m = meilleurPartage({ forme: statut, enveloppe: benefice, activite, foyer: {} });
    const p = m.meilleur;
    reponses["remuneration-ou-dividendes"] = {
        statut: "ok",
        type: !m.partageUtile
            ? "partage_remuneration"
            : p.part < 0.005
              ? "partage_dividendes"
              : "partage_mixte",
        remuneration: statut === "sasu" ? p.remunerationBrute : p.remunerationAvantImpot,
        gain: m.gain,
    };
    // L'ARRÊT MALADIE, AVEC LE MÊME PARTAGE. C'est la réponse qui surprend le
    // plus : le meilleur partage d'une SASU est souvent tout en dividendes, et
    // il ne couvre alors rien. Un arrêt qui commence aujourd'hui, sur un
    // salaire régulier ; en EURL, un gérant affilié depuis plus d'un an.
    const debutArret = aujourdhui instanceof Date ? aujourdhui : new Date();
    const debut = new Date(Date.UTC(debutArret.getUTCFullYear(), debutArret.getUTCMonth(), debutArret.getUTCDate(), 12));
    if (statut === "sasu") {
        const a = arretSasu({ salaireMensuel: (p.remunerationBrute || 0) / 12, debut, jours: 30 });
        reponses["arret-maladie-societe"] = a.ouvre
            ? { statut: "ok", type: "ij_jour", parJour: a.parJour }
            : { statut: "attention", type: "ij_rien_sasu", seuil: a.salaireMensuelOuverture };
    } else if (statutCalculable("eurl", activite)) {
        const a = arretEurl({ revenuAnnuel: (p.remunerationAvantImpot || 0) + (p.dividendesCotisants || 0), jours: 30 });
        reponses["arret-maladie-societe"] = a.ouvre
            ? { statut: "ok", type: "ij_jour", parJour: a.parJour }
            : { statut: "attention", type: "ij_rien_eurl", seuil: a.revenuMinimum };
    }

    if (statutCalculable("eurl", activite)) {
        const f = comparerFormes({ enveloppe: benefice, activite, foyer: {} });
        reponses["eurl-ou-sasu"] = {
            statut: "ok",
            type: `formes_${f.gagnant || "egal"}`,
            ecart: Math.abs(f.ecart),
        };
    }
    return reponses;
}

/**
 * Les réponses des outils, par clé d'outil.
 *
 * @returns { [cleOutil]: { statut, type, ...valeurs } } où statut vaut
 *   « urgent », « attention », « ok » ou « info », et type désigne la phrase
 *   que la page doit employer.
 */
export function reponsesDesOutils({
    activite,
    objectifMensuel,
    tarifJournalier,
    premiereAnnee = false,
    dateDebut,
    aujourdhui = new Date(),
    statut = "micro",
}) {
    const reponses = {};

    if (estHorsMicro(statut)) {
        return reponsesHorsMicro({ statut, activite, objectifMensuel, dateDebut, aujourdhui });
    }

    // La date, quand elle est connue, remplace ce que disait la case à cocher.
    const anciennete = dateDebut instanceof Date
        ? ancienneteDepuis(dateDebut, aujourdhui)
        : { premiereAnnee, regimeAcquis: premiereAnnee };
    premiereAnnee = anciennete.premiereAnnee;

    if (activite && PLAFONDS[activite] && objectifMensuel > 0) {
        const ca = caPourSeVerser({
            objectifAnnuel: objectifMensuel * 12,
            activite,
            premiereAnnee,
        });

        if (ca === null) {
            // L'objectif dépasse ce que le régime peut verser : deux outils
            // ont quelque chose à dire, et c'est la même chose.
            reponses["plafonds"] = {
                statut: "urgent",
                type: "horsPortee",
                plafond: PLAFONDS[activite],
            };
            reponses["micro-reel-ou-societe"] = {
                statut: "urgent",
                type: "horsPortee",
                plafond: PLAFONDS[activite],
            };
        } else {
            const caMensuel = ca / 12;

            reponses["revenu-net"] = {
                statut: "ok",
                type: "facturer",
                caMensuel,
                objectifMensuel,
            };

            if (tarifJournalier > 0) {
                reponses["combien-facturer"] = {
                    statut: "ok",
                    type: "jours",
                    jours: Math.ceil(caMensuel / tarifJournalier),
                    tarifJournalier,
                };
            }

            // Le plafond. Les deux premières années, le régime est acquis
            // quel que soit le chiffre ; ensuite, une seule année au-dessus ne
            // fait sortir de rien, il en faut deux de suite (statutMicro).
            const plafond = PLAFONDS[activite];
            const part = ca / plafond;
            if (anciennete.regimeAcquis) {
                reponses["plafonds"] = { statut: "ok", type: "plafondAcquis", part, plafond };
            } else if (part > 1) {
                reponses["plafonds"] = { statut: "attention", type: "plafondDepasse", part, plafond };
            } else {
                reponses["plafonds"] = {
                    statut: part >= 0.8 ? "attention" : "ok",
                    type: "plafond",
                    part,
                    plafond,
                };
            }

            const tva = reponseTva({ activite, ca, dateDebut, aujourdhui });
            if (tva) reponses["seuils-tva"] = tva;

            const retraite = trimestresRetraite({ chiffreAffaires: ca, activite });
            reponses["trimestres-retraite"] = {
                statut: retraite.valides >= 4 ? "ok" : "attention",
                type: retraite.valides >= 4 ? "retraitePleine" : "retraitePartielle",
                valides: retraite.valides,
                caProchain: retraite.caProchainTrimestre,
            };
        }
    }

    if (dateDebut instanceof Date && !Number.isNaN(dateDebut.getTime())) {
        for (const e of echeancesPremiereAnnee({ dateDebut, aujourdhui })) {
            if (e.passee) continue;
            const cle = OUTIL_DE_L_ECHEANCE[e.cle];
            if (!cle) continue;
            reponses[cle] = {
                statut: statutDeLEcheance(e.joursRestants),
                type: `echeance_${e.cle}`,
                date: e.date,
                joursRestants: e.joursRestants,
            };
        }
    }

    return reponses;
}
